// ============================================
// SCATTER VIEW - Two-axis employee scatter plot
// ============================================

import { useMemo } from 'react';
import {
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, Cell, Legend
} from 'recharts';
import { ArrowLeft } from 'lucide-react';
import type { Employee } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import type { DemographicDimension } from '../../constants/demographics';
import { groupEmployeesByDemographic, getSegmentLabel, getSegmentColor } from '../../services/demographics';

interface ScatterViewProps {
    employees: Employee[];
    axes: [string, string];
    onAxesChange: (axes: [string, string]) => void;
    demographicDimension?: DemographicDimension | null;
}

export default function ScatterView({ employees, axes, onAxesChange, demographicDimension }: ScatterViewProps) {
    const categories = useMemo(() => {
        if (employees.length === 0) return [];
        return Object.keys(employees[0].scores);
    }, [employees]);

    const demographicGroups = useMemo(() => {
        if (!demographicDimension) return null;
        return groupEmployeesByDemographic(employees, demographicDimension);
    }, [employees, demographicDimension]);

    const scatterGroups = useMemo(() => {
        if (!demographicGroups) {
            const data = employees.map(e => ({
                name: e.name,
                x: e.scores[axes[0]] ?? 0,
                y: e.scores[axes[1]] ?? 0,
            }));
            return [{ key: 'all', label: 'Employees', color: '#133437', data }];
        }

        const groups: { key: string; label: string; color: string; data: any[] }[] = [];
        demographicGroups.forEach((emps, segKey) => {
            groups.push({
                key: segKey,
                label: getSegmentLabel(segKey, demographicDimension!),
                color: getSegmentColor(segKey),
                data: emps.map(e => ({
                    name: e.name,
                    x: e.scores[axes[0]] ?? 0,
                    y: e.scores[axes[1]] ?? 0,
                })),
            });
        });
        return groups;
    }, [employees, axes, demographicGroups, demographicDimension]);

    if (employees.length === 0 || categories.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין נתונים להצגה</p>
            </div>
        );
    }

    return (
        <div className="analytics-scatter-container" style={{ direction: 'ltr' }}>
            {/* Axis Selectors */}
            <div className="analytics-scatter-controls">
                <div className="analytics-scatter-control">
                    <label>ציר X</label>
                    <select
                        value={axes[0]}
                        onChange={(e) => onAxesChange([e.target.value, axes[1]])}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{getHebrewName(cat)}</option>
                        ))}
                    </select>
                </div>
                <div className="analytics-scatter-arrow">
                    <ArrowLeft size={20} />
                </div>
                <div className="analytics-scatter-control">
                    <label>ציר Y</label>
                    <select
                        value={axes[1]}
                        onChange={(e) => onAxesChange([axes[0], e.target.value])}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{getHebrewName(cat)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Chart */}
            <div className="analytics-scatter-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            type="number"
                            dataKey="x"
                            name={getHebrewName(axes[0])}
                            domain={[0, 10]}
                            ticks={[0, 2, 4, 6, 8, 10]}
                            tick={{ fontSize: 10 }}
                            label={{
                                value: getHebrewName(axes[0]),
                                position: 'bottom',
                                offset: 0,
                                fontSize: 12
                            }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            name={getHebrewName(axes[1])}
                            domain={[0, 10]}
                            ticks={[0, 2, 4, 6, 8, 10]}
                            tick={{ fontSize: 10 }}
                            label={{
                                value: getHebrewName(axes[1]),
                                angle: -90,
                                position: 'insideLeft',
                                fontSize: 12
                            }}
                        />
                        <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const d = payload[0].payload;
                                    return (
                                        <div style={{
                                            backgroundColor: '#ffffff',
                                            borderRadius: '12px',
                                            border: '1px solid #ebe6de',
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                            direction: 'rtl',
                                            textAlign: 'right',
                                            padding: '12px',
                                            fontSize: '13px'
                                        }}>
                                            <p style={{ fontWeight: 800, marginBottom: '4px', borderBottom: '1px solid #f4f1eb', paddingBottom: '4px' }}>{d.name}</p>
                                            <div style={{ color: '#938977', marginBottom: '2px' }}>{getHebrewName(axes[0])}: <b style={{ color: '#133437' }}>{d.x.toFixed(2)}</b></div>
                                            <div style={{ color: '#938977' }}>{getHebrewName(axes[1])}: <b style={{ color: '#133437' }}>{d.y.toFixed(2)}</b></div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <ReferenceLine x={5.5} stroke="#dfd8cd" strokeDasharray="3 3" />
                        <ReferenceLine y={5.5} stroke="#dfd8cd" strokeDasharray="3 3" />
                        {demographicDimension && (
                            <Legend
                                content={() => (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '20px',
                                        direction: 'rtl',
                                        paddingTop: '12px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {scatterGroups.map(group => (
                                            <div key={group.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    backgroundColor: group.color,
                                                    display: 'inline-block',
                                                    flexShrink: 0
                                                }} />
                                                <span style={{ fontSize: '12px', color: '#5c5549', whiteSpace: 'nowrap' }}>{group.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                        )}
                        {scatterGroups.map(group => (
                            <Scatter key={group.key} name={group.label} data={group.data}>
                                {group.data.map((_, index) => (
                                    <Cell key={`cell-${group.key}-${index}`} fill={group.color} opacity={0.7} />
                                ))}
                            </Scatter>
                        ))}
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
